import React, { useState, useEffect, useMemo } from "react";
import {
	Grid,
	LinearProgress,
	Typography,
} from "@material-ui/core";
import { Button, IconButton } from '@coolremote/shared';
import { Add } from "@material-ui/icons";
import clsx from "clsx";
import { find, isUndefined, setWith, sortBy, cloneDeep, filter, groupBy } from "lodash";
import { useStoreActions, useStoreState } from "@models/RootStore";
import { t } from "ttag";
import { useIsModbusEnabled } from "../Modbus/modbusFeature";
import Delete from "../../components/Delete/Delete";
import LightTooltip from "../../components/Tooltip/LightTooltip";
import {
	CoolModeOff as SvgCoolModeOff,
	CoolModeOn as SvgCoolModeOn,
	Disconnect as SvgDisconnect,
	HeatModeOff as SvgHeatModeOff,
	HeatModeOn as SvgHeatModeOn,
	MoreVert,
	OnGrey as SvgPowerOff,
	OnWhite as SvgPowerOn,
} from "../../icons/";
import { ScheduleOffIcon } from "../../icons/index";
import { EditIcon } from "../../logos";
import assetsService from "../../services/assetsService";
import { systemViewStyle } from "./SystemView.style";
import { IServiceTypes } from "@models/Types";
import { ISensor } from "@models/Sensors";

interface System {
	name: string;
	brandNum: number;
	numOfControlUnits: number;
	numOfOutdoorUnits: number;
	numOfBsBoxUnits: number;
	id: string;
	hasDisconnectedUnits: boolean;
	isMapping?: boolean;
	schedules: Array<any>; // Define a more specific type if possible
	mappingCurrent: number;
	mappingTotal: number;
	permissions: {
		canControl: boolean;
		canDelete: boolean;
		canUpdate: boolean;
		canCreateSchedules: boolean;
	};
	operationMode: string;
	operationStatus: string;
	internalId?: string | number;
}

interface Actions {
	openEditSensors: (deviceId: string, line?: string | number) => void;
	openSystemSchedule: (id: string, deviceId: string, system: System) => void;
	fetchData: () => void;
	setOpenMappingInProgress: (isOpen: boolean) => void;
	openAddEditSystem: (deviceId: string, systemId: string) => void;
	changeSystemMode: (systemId: string, mode: string) => void;
	changeSystemStatus: (systemId: string, status: string) => void;
	setHighlightedId?: (id: string) => void;
	openEditModbusUnits?: (device: any, line: any) => void;
}

interface Device {
	id: string;
	numOfSensors: number;
	numOfPowerMeters?: number;
	numOfDisconnectedSensors?: number;
	numOfDisconnectedAuxDevices?: number;
	numOfDisconnectedPowerMeters?: number;
	sensors?: Array<{ isConnected?: boolean }>;
	powerMeters?: Array<{ isConnected?: boolean }>;
	permissions?: DevicePermissions;
	unassignedLines: {
		[lineId: string]: {
			outdoor?: number;
			control?: number;
			service?: number;
			bsbox?: number;
		};
	};
	isConnected: boolean;
	/** Device brand; compared against `deviceBrands.ca_ng` to detect NG (EDGE) devices. */
	type?: number | string;
}

interface SystemUnitsInfoProps {
	outdoor?: number;
	indoor?: number;
	service?: number;
	bsbox?: number;
	internalId?: number | string;
	forceHideOutdoor?: boolean;
}

const SystemUnitsInfo: React.FC<SystemUnitsInfoProps> = ({
	outdoor,
	indoor,
	service,
	bsbox,
	internalId,
	forceHideOutdoor,
}) => {
	const classes = systemViewStyle();
	const { outdoor: outdoorClass, indoor: indoorClass, indoorOutdoorContainer } = classes;

	const renderUnits = (count: number | undefined, label: string, className: string) => {
		if (!count) {
			return null;
		}
		return (
			<div>
				<Typography className={clsx("", className)} variant="body1">
					<span>
						{count} {label}
					</span>
				</Typography>
			</div>
		);
	};

	return (
		<div className={indoorOutdoorContainer}>
			{!forceHideOutdoor && renderUnits(outdoor, t`Outdoor`, outdoorClass)}
			{renderUnits(indoor, t`Indoor`, indoorClass)}
			{renderUnits(service, t`Service`, indoorClass)}
			{renderUnits(bsbox, t`BS`, indoorClass)}
			{internalId !== undefined && (
				<div className="SysInternalId">
					<Typography className={clsx("", indoorClass)} variant="body1">
						<span>
							{t`Sys#:`} {internalId}
						</span>
					</Typography>
				</div>
			)}
		</div>
	);
};

interface SiteSystemProps {
	system: System;
	actions: Actions & { setHighlightedId?: (id: string) => void };
	device: Device;
	highlightedId?: string;
	hasOnlyControlUnit: boolean;
}

const SiteSystem: React.FC<SiteSystemProps> = ({
	system,
	actions,
	device,
	highlightedId,
	hasOnlyControlUnit,
}) => {
	const classes = systemViewStyle();
	const deleteSystem = useStoreActions((action) => action.deleteSystem);
	const types: IServiceTypes = useStoreState((state) => state.types);
	const {
		hvacBrands,
		operationModesExtended: typesOperationModes,
		operationStatuses: typesOperationStatuses,
	} = types;
	const altermaBrandNum =
		hvacBrands?.filter(
			(brand: any) => brand?.type?.toUpperCase() === "ALTHERMA"
		)[0]?.value || 10;

	const {
		overWriteDisplay,
		iconButton,
		powerButton,
		powerButtonDisabled,
		PowerButtonOperationStatusContainer,
		systemName,
		logo,
		groupIcons,
		nameModelContainer,
		powerButtonSVG,
		modeButton,
		powerOff,
		powerOn,
		modeButtonDisabled,
		indoorOutdoorContainer,
		outdoor,
		indoor,
		controlContainer,
		SiteSystem,
	} = classes;

	const {
		name,
		brandNum,
		numOfControlUnits,
		numOfOutdoorUnits,
		numOfBsBoxUnits,
		id,
		hasDisconnectedUnits,
		isMapping = false, //system or line differs?
		schedules = [],
		mappingCurrent,
		mappingTotal,
		permissions,
		operationMode,
		operationStatus,
		internalId
	} = system;

	const { canControl, canDelete, canUpdate, canCreateSchedules } = permissions;

	const isWaterHeater = find(hvacBrands, { value: brandNum })
		? find(hvacBrands, { value: brandNum }).isWaterHeater
		: false;
	const isPowerOn = typesOperationStatuses[operationStatus] === "on";
	const isSystemInHeatMode = typesOperationModes[operationMode] === "HEAT";
	const isSystemInCoolMode = typesOperationModes[operationMode] === "COOL";
	const systemHasControlUnits = !!numOfControlUnits;
	const isAlterma = +brandNum === +altermaBrandNum;
	const hasSchedules = schedules.length > 0;
	const { changeSystemMode, changeSystemStatus } = actions;

	const delSystem = (idObj: any) => {
		deleteSystem(idObj).then(() => {
			actions.fetchData();
		});
	};

	const computedInternalId = useMemo(() => {
		//If the system is Mitsubishy Electric (4) convert the internalId to base 10 from base 16.
		if (brandNum === 4 && internalId !== undefined) {
			return parseInt(`${internalId}`, 16);
		}
		return internalId;
	}, [internalId]);

	return (
		<div className={clsx(classes.BaseTile, classes.SiteSystem)}>
			<div className={overWriteDisplay}>
				<Typography className={systemName}>{name}</Typography>
				<div className={groupIcons}>
					<LightTooltip title={t`Edit system information - unit names, mapping control /service addreses, assigining units between systems`}>
						<IconButton
							disableRipple
							disabled={!canUpdate}
							onClick={(e: any) => {
								e.stopPropagation();
								if (isMapping) {
									actions.setOpenMappingInProgress(true);
								} else {
									actions.setHighlightedId?.(`${device.id}-${id}`);
									actions.openAddEditSystem(device.id, id);
								}
							}}
							className={classes.iconBtnStyle}
						>
							<EditIcon />
						</IconButton>
					</LightTooltip>
					<Delete
						classes={{ root: iconButton }}
						type={t`System`}
						object={system}
						detach={delSystem}
						disabled={isMapping || !canDelete}
					></Delete>
				</div>
			</div>
			<div className={nameModelContainer}>
				<div className={classes.minLogoHeight}>
					{brandNum ? (
						<img
							height={25}
							src={assetsService.getBrandLogo(`${brandNum}-small.svg`)}
							className={logo}
							onError={(event: any) => {
								return (event.target.src =
									assetsService.getBrandLogo("empty.png"));
							}}
							alt={
								find(hvacBrands, { value: brandNum })
									? find(hvacBrands, {
										value: brandNum,
									}).name
									: ""
							}
						/>
					) : (
						""
					)}
				</div>
				<div className={classes.minLogoHeight}>
					{hasDisconnectedUnits && <SvgDisconnect />}
				</div>
			</div>
			<SystemUnitsInfo
				forceHideOutdoor={isAlterma || hasOnlyControlUnit}
				outdoor={numOfOutdoorUnits}
				indoor={numOfControlUnits}
				internalId={computedInternalId}
				bsbox={numOfBsBoxUnits}
			/>
			{isMapping ? (
				<div className={classes.mappingContainer}>
					<Typography className={classes.mappingText}>
						{t`Mapping`}... ({mappingCurrent}/{mappingTotal})
						<span>
							{mappingCurrent === 0 && mappingTotal === 0
								? 100
								: Math.round((mappingCurrent / mappingTotal) * 100)}
							%
						</span>
					</Typography>
					<Typography className={classes.mappingTime}>
						{(Math.round((mappingTotal - mappingCurrent) / 2) * 100) / 100}{" "}
						{t`min left`}
					</Typography>
					<LinearProgress
						classes={{
							root: classes.progressRoot,
							bar: classes.progressBar,
						}}
						variant="determinate"
						value={
							mappingCurrent === 0 && mappingTotal === 0
								? 100
								: Math.round((mappingCurrent / mappingTotal) * 100)
						}
					/>
				</div>
			) : (
				<div className={PowerButtonOperationStatusContainer}>
					<LightTooltip title={t`Change mode (cool/heat) for the whole system. Mode change triggers a process of turning OFF all units in the system and then changing the mode. Note: units will not start automatically after the mode change to avoid conflicts.`}>
						<div>
							{isSystemInCoolMode ? (
								<SvgCoolModeOn
									onClick={(e: any) => {
										e.stopPropagation();
										!canControl ? {} : changeSystemMode(id, "COOL");
									}}
									className={clsx(
										modeButton,
										(!canControl || !systemHasControlUnits) && modeButtonDisabled
									)}
								/>
							) : (
								<SvgCoolModeOff
									onClick={(e: any) => {
										e.stopPropagation();
										!canControl ? {} : changeSystemMode(id, "COOL");
									}}
									className={clsx(
										modeButton,
										(!canControl || !systemHasControlUnits) && modeButtonDisabled
									)}
								/>
							)}
							{isSystemInHeatMode ? (
								<SvgHeatModeOn
									onClick={(e: any) => {
										e.stopPropagation();
										!canControl ? {} : changeSystemMode(id, "HEAT");
									}}
									className={clsx(
										modeButton,
										(!canControl || !systemHasControlUnits) && modeButtonDisabled
									)}
								/>
							) : (
								<SvgHeatModeOff
									onClick={(e: any) => {
										e.stopPropagation();
										!canControl ? {} : changeSystemMode(id, "HEAT");
									}}
									className={clsx(
										modeButton,
										(!canControl || !systemHasControlUnits) && modeButtonDisabled
									)}
								/>
							)}
						</div>
					</LightTooltip>
					{!isWaterHeater && (
						<IconButton
							disableFocusRipple
							disableRipple
							disabled={!canCreateSchedules}
							onClick={(e: any) => {
								e.stopPropagation();
								actions.openSystemSchedule(id, device.id, system);
							}}
							name="schedule"
							className={classes.scheduleIconBtn}
						>
							<ScheduleOffIcon
								color="#AAA2AA"
								className={classes.scheduleIcon}
							/>
							{hasSchedules && (
								<span className={classes.badge}>{schedules.length}</span>
							)}
						</IconButton>
					)}
					<div className={controlContainer}>
						<LightTooltip title={t`Turn ON/OFF all units of the system.`}>
							<IconButton
								disabled={!canControl}
								onClick={(e: any) => {
									e.stopPropagation();
									!canControl ? {} : changeSystemStatus(id, operationStatus);
								}}
								className={clsx(
									powerButton,
									(!canControl || !systemHasControlUnits) && powerButtonDisabled
								)}
							>
								{isPowerOn ? (
									<div className={powerOn}>
										<SvgPowerOn className={powerButtonSVG} />
									</div>
								) : (
									<div className={powerOff}>
										<SvgPowerOff className={powerButtonSVG} />
									</div>
								)}
							</IconButton>
						</LightTooltip>
					</div>
				</div>
			)}
		</div>
	);
};

interface Permissions {
	canUpdate: boolean;
}

interface AuxDevicesProps {
	device: Device;
	data: {
		line: any;
		/** This device's sensors on this line, from the site-wide sensors fetch. */
		sensors: ISensor[];
	};
	actions: Actions;
}

const AuxDevices: React.FC<AuxDevicesProps> = ({ actions, device, data }) => {
	const classes = systemViewStyle();
	const { systemName, powerMetereGroupIcons, indoorOutdoorContainer, outdoor } = classes;
	const { line, sensors } = data;
	const deviceBrands = useStoreState((state) => state.types.deviceBrands);
	const isNG = deviceBrands?.ca_ng != null && device.type == deviceBrands.ca_ng;

	const lineVal = useMemo(() => {
		if (isNaN(line)) {
			return t`unknown line`;
		}

		if (line == 0) {
			return isNG ? t`GPIO` : t`IP Aux Devices`;
		}

		return t`L${line} Aux Devices`;
	}, [line, isNG]);

	const sensorsCount = sensors?.length ?? 0;

	const hasDisconnectedSensors = useMemo(
		() => !device.isConnected || sensors?.some((sensor: ISensor) => !sensor?.isConnected),
		[sensors, device.isConnected]
	);

	const handleEditClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		actions.setHighlightedId?.(`${device?.id}-AuxDevices-${line}`);
		actions.openEditSensors(device?.id, line);
	};

	return (
		<Grid key={`ppd-details`} className={clsx(classes.BaseTile, classes.SiteSensor)}>
			<div
				className={classes.overWriteDisplayVirtualSystems}
			>
				<Typography className={systemName || ""}>{lineVal}</Typography>
				<div className={powerMetereGroupIcons}>
					<LightTooltip title={t`Edit system information - unit names, mapping control /service addreses, assigining units between systems`}>
						<IconButton
							disableRipple
							disabled={!device?.permissions?.canUpdate}
							onClick={handleEditClick}
							className={classes.iconBtnStyle}
						>
							<EditIcon />
						</IconButton>
					</LightTooltip>
					{hasDisconnectedSensors && <SvgDisconnect />}
				</div>
			</div>
			<div className={indoorOutdoorContainer}>
				<div>
					<Typography className={clsx("", outdoor)} variant="body1">
						<span>
							{sensorsCount} {t` Devices`}
						</span>
					</Typography>
				</div>
			</div>
		</Grid>
	);
};

interface PowerMetersProps {
	device: {
		id: string;
		numOfPowerMeters: number;
		numOfDisconnectedPowerMeters?: number;
		powerMeters?: Array<{ isConnected?: boolean }>;
		permissions?: {
			canUpdate: boolean;
		};
	};
	actions: {
		openEditPPD: (deviceId: string) => void;
		setHighlightedId?: (id: string) => void;
	};
}

const PowerMeters: React.FC<PowerMetersProps> = ({ device, actions }) => {
	const classes = systemViewStyle();
	const { systemName, indoorOutdoorContainer, outdoor, powerMetereGroupIcons } = classes;
	const disconnectedPowerMetersCount =
		device?.numOfDisconnectedPowerMeters ??
		(device?.powerMeters?.filter((powerMeter: any) => !powerMeter?.isConnected).length || 0);
	const hasDisconnectedPowerMeters = disconnectedPowerMetersCount > 0;

	return (
		<Grid key={`ppd-details`} className={clsx(classes.BaseTile, classes.SiteSystem)}>
			<div className={classes.overWriteDisplayVirtualSystems}>
				<Typography className={systemName || ""}>{t` Power Meters`}</Typography>
				<div className={powerMetereGroupIcons}>
					<LightTooltip title={t`Edit system information - unit names, mapping control /service addreses, assigining units between systems`}>
						<IconButton
							disableRipple
							disabled={!device?.permissions?.canUpdate}
							onClick={(e: any) => {
								e.stopPropagation();
								actions.setHighlightedId?.(`${device.id}-PowerMeter`);
								actions.openEditPPD(device.id);
							}}
							className={classes.iconBtnStyle}
						>
							<EditIcon />
						</IconButton>
					</LightTooltip>
					{hasDisconnectedPowerMeters && <SvgDisconnect />}
				</div>
			</div>
			<div className={indoorOutdoorContainer}>
				<div>
					<Typography className={clsx("", outdoor)} variant="body1">
						<span>
							{device.numOfPowerMeters} {t` Power Meters`}
						</span>
					</Typography>
				</div>
			</div>
		</Grid>
	);
};

interface BACnetProps {
	device: {
		id: string;
		bacnetUnits: [];
		permissions?: {
			canUpdate: boolean;
		};
	};
	data: any;
	actions: {
		openEditBacnetUnits: (device: any, line: any) => void;
		setHighlightedId?: (id: string) => void;
	};
}

const BACnetDevices: React.FC<BACnetProps> = ({ device, actions, data }) => {
	const classes = systemViewStyle();
	const { systemName, groupIcons, indoorOutdoorContainer, outdoor } = classes;
	const { line } = data;

	const lineVal = useMemo(() => {
		if (isNaN(line)) {
			return 'unknown line';
		}

		if (line == 0) {
			return 'IP BACnet';
		}

		return `L${line} BACnet`;
	}, [line]);

	const bacnetUnitsCount = useMemo(() => {
		if (!device?.bacnetUnits?.length) {
			return 0;
		}

		return filter(device.bacnetUnits, (entry: { line: any, isEngaged: boolean }) => {
			return entry.line == line && entry.isEngaged;
		}).length;
	}, [device.bacnetUnits]);

	return (
		<Grid key={`ppd-details`} className={clsx(classes.BaseTile, classes.SiteSystem)}>
			<div className={classes.overWriteDisplayVirtualSystems}>
				<Typography className={systemName || ""}>{lineVal} - {t`BACnet devices`}</Typography>
				<div className={groupIcons}>
					<IconButton
						disableRipple
						disabled={!device?.permissions?.canUpdate}
						onClick={(e: any) => {
							e.stopPropagation();
							actions.setHighlightedId?.(`${device.id}-BACnetDevices-${line}`);
							actions.openEditBacnetUnits(device, line);
						}}
						className={classes.iconBtnStyle}
					>
						<EditIcon />
					</IconButton>
				</div>
			</div>
			<div className={indoorOutdoorContainer}>
				<div>
					<Typography className={clsx("", outdoor)} variant="body1">
						<span>
							{bacnetUnitsCount} {t`BACnet devices`}
						</span>
					</Typography>
				</div>
			</div>
		</Grid>
	);
};

interface ModbusProps {
	device: {
		id: string;
		permissions?: {
			canUpdate: boolean;
		};
	};
	data: {
		line: any;
		/** Engaged modbus units on this line, joined with their device's `line`/`isConnected`. */
		modbusUnits: any[];
		id: string;
		highlightedId?: string;
	};
	actions: {
		openEditModbusUnits?: (device: any, line: any) => void;
		setHighlightedId?: (id: string) => void;
	};
}

const ModbusDevices: React.FC<ModbusProps> = ({ device, actions, data }) => {
	const classes = systemViewStyle();
	const { systemName, powerMetereGroupIcons, indoorOutdoorContainer, outdoor } = classes;
	const { line, modbusUnits } = data;

	const lineVal = useMemo(() => {
		if (isNaN(line)) {
			return 'unknown line';
		}

		if (line == 0) {
			return 'IP Modbus';
		}

		return `L${line} Modbus`;
	}, [line]);

	// Already filtered to engaged units on this line when the tile was built.
	const modbusUnitsCount = modbusUnits?.length ?? 0;

	const hasDisconnectedUnits = useMemo(
		() => !!modbusUnits?.some((entry: { isConnected?: boolean }) => !entry.isConnected),
		[modbusUnits]
	);

	return (
		<Grid key={`modbus-details`} className={clsx(classes.BaseTile, classes.SiteSystem)}>
			<div className={classes.overWriteDisplayVirtualSystems}>
				<Typography className={systemName || ""}>{lineVal}</Typography>
				<div className={powerMetereGroupIcons}>
					<IconButton
						disableRipple
						disabled={!device?.permissions?.canUpdate}
						onClick={(e: any) => {
							e.stopPropagation();
							actions.setHighlightedId?.(`${device.id}-ModbusDevices-${line}`);
							actions.openEditModbusUnits?.(device, line);
						}}
						className={classes.iconBtnStyle}
					>
						<EditIcon />
					</IconButton>
					{hasDisconnectedUnits && <SvgDisconnect />}
				</div>
			</div>
			<div className={indoorOutdoorContainer}>
				<div>
					<Typography className={clsx("", outdoor)} variant="body1">
						<span>
							{modbusUnitsCount} {t`Modbus devices`}
						</span>
					</Typography>
				</div>
			</div>
		</Grid>
	);
};

interface Line {
	isMapping?: boolean;
	mappingCurrent: number;
	mappingTotal: number;
	line: string;
}

interface DevicePermissions {
	canUpdate: boolean;
}
interface Actions {
	setOpenMappingInProgress: (isOpen: boolean) => void;
	openCloseAssignLineUnits: (
		deviceId: string,
		lineId: string,
		isService: boolean
	) => void;
	setHighlightedId?: (id: string) => void;
}

interface UnassignedLinesProps {
	line: Line;
	device: Device;
	actions: Actions;
	isService: boolean;
}

const UnassignedLines: React.FC<UnassignedLinesProps> = ({
	line,
	device,
	actions,
	isService,
}) => {
	const classes = systemViewStyle();
	const {
		overWriteDisplay,
		systemName,
		groupIcons,
		indoorOutdoorContainer,
		outdoor,
		indoor,
	} = classes;

	const {
		isMapping = false,
		mappingCurrent,
		mappingTotal,
		line: lineId,
	} = line;
	return (
		<Grid
			key={`unassigned-line-${lineId}`}
			className={clsx(classes.BaseTile, classes.UnassignedLines)}
			style={{ backgroundColor: "#fff0f0" }}
		>
			<div className={overWriteDisplay}>
				<Typography className={systemName}>
					{`Line ${lineId} unassigned Units`}
				</Typography>
				<div className={groupIcons}>
					<LightTooltip title={t`Edit system information - unit names, mapping control /service addreses, assigining units between systems`}>
						<IconButton
							disableRipple
							disabled={!device?.permissions?.canUpdate}
							onClick={(e: any) => {
								e.stopPropagation();
								if (isMapping) {
									actions.setOpenMappingInProgress(true);
								} else {
									actions.setHighlightedId?.(`${device.id}-UnassignedLines-${lineId}`);
									actions.openCloseAssignLineUnits(
										device.id,
										lineId,
										isService
									);
								}
							}}
							className={classes.iconBtnStyle}
						>
							<EditIcon />
						</IconButton>
					</LightTooltip>
				</div>
			</div>
			<SystemUnitsInfo
				outdoor={device.unassignedLines[lineId]?.outdoor}
				indoor={device.unassignedLines[lineId]?.control}
				service={device.unassignedLines[lineId]?.service}
				bsbox={device.unassignedLines[lineId]?.bsbox}
			/>
			{isMapping && (
				<div className={classes.mappingContainer}>
					<Typography className={classes.mappingText}>
						{t`Mapping`}... ({mappingCurrent}/{mappingTotal})
						<span>
							{mappingCurrent === 0 && mappingTotal === 0
								? 100
								: Math.round((mappingCurrent / mappingTotal) * 100)}
							%
						</span>
					</Typography>
					<Typography className={classes.mappingTime}>
						{(Math.round((mappingTotal - mappingCurrent) / 2) * 100) / 100}{" "}
						{t`min left`}
					</Typography>
					<LinearProgress
						classes={{
							root: classes.progressRoot,
							bar: classes.progressBar,
						}}
						variant="determinate"
						value={
							mappingCurrent === 0 && mappingTotal === 0
								? 100
								: Math.round((mappingCurrent / mappingTotal) * 100)
						}
					/>
				</div>
			)}
		</Grid>
	);
};

interface ViewWidgetProps {
	widgetType:
	| "SiteSystem"
	| "PowerMeter"
	| "AuxDevices"
	| "UnassignedLines"
	| "BACnetDevices"
	| "ModbusDevices"
	| string;
	data: any; // Consider replacing 'any' with a more specific type based on the data structure
	actions: Actions & { setHighlightedId?: (id: string) => void };
	device: Device;
	isService: boolean;
	hasOnlyControlUnit: boolean;
	highlightedId?: string;
}

const ViewWidget: React.FC<ViewWidgetProps> = (props) => {
	const { widgetType, data } = props;

	if (widgetType === "SiteSystem") {
		// @ts-ignore
		return <SiteSystem {...props} system={data} />;
	}

	if (widgetType === "PowerMeter") {
		// @ts-ignore
		return <PowerMeters {...props} />;
	}

	if (widgetType === "AuxDevices") {
		// @ts-ignore
		return <AuxDevices {...props} />;
	}

	if (widgetType === "UnassignedLines") {
		// @ts-ignore
		return <UnassignedLines {...props} line={data} />;
	}

	if (widgetType === "BACnetDevices") {
		// @ts-ignore
		return <BACnetDevices {...props} />;
	}

	if (widgetType === "ModbusDevices") {
		// @ts-ignore
		return <ModbusDevices {...props} />;
	}

	return <></>;
};

interface ISystemsViewProps {
	device: any;
	actions: any;
	isService: boolean;
	hasOnlyControlUnit: boolean;
	siteId: string | number;
	highlightedId?: string;
	/**
	 * All modbus units on the site, keyed by unit id, each joined with its
	 * modbus device's `line` and `isConnected`. Fetched once per site by
	 * SiteView — the site-management payload has no `line` on modbus units.
	 */
	siteModbusUnits?: Record<string, any> | null;
	/**
	 * All sensors (aux devices) on the site, each carrying its own `line` and
	 * `device`. Fetched once per site by SiteView — the site-management
	 * payload only has a numOfSensors count per device, not the sensors.
	 */
	siteSensors?: ISensor[] | null;
}
const SystemsView = (props: ISystemsViewProps) => {
	const classes = systemViewStyle();
	const {
		device,
		actions,
		isService,
		hasOnlyControlUnit = false,
		siteId,
		highlightedId: highlightedIdFromProps,
		siteModbusUnits,
		siteSensors,
	} = props;

	const [isTileDraggable, setIsTileDraggable] = useState<any>({});
	const [draggedTile, setDraggedTile] = useState<any>(null);
	const [dragOverTile, setDragOverTile] = useState<any>(null);
	const [tiles, setTiles] = useState<any>([]);
	const highlightedId = highlightedIdFromProps;
	const userPreferences = useStoreState((state) => state.users.userPreferences);
	const updateUserPreferences = useStoreActions(
		(action) => action.users.updateUserPreferences
	);
	const selections = useStoreState((state) => state.selections.selections);
	const { customerId } = selections;

	const { enableBacnet } = useStoreState((s) => s.config);
	const enableModbus = useIsModbusEnabled();

	if (isUndefined(device)) {
		return null;
	}

	const {
		unassignedLines,
		bacnetUnits,
		systems,
		numOfPowerMeters,
		id: deviceId,
	} = device;

	// Engaged modbus units for THIS device, grouped by their device's line.
	// Line 7 is dropped — that's the line modbus units land on when they are
	// configured as universal units, and those must not surface as a line tile.
	const modbusUnitsByLine = useMemo<Record<string, any[]>>(() => {
		const engagedForDevice = filter(
			siteModbusUnits || {},
			(entry: any) =>
				String(entry.device) === String(deviceId) &&
				entry.isEngaged &&
				entry.line != null &&
				+entry.line !== 7
		);
		return groupBy(engagedForDevice, 'line');
	}, [siteModbusUnits, deviceId]);

	// This device's sensors, grouped by line. Unlike modbus, there is no
	// "universal unit" line to drop here — every line, including 0 (e.g. Dry
	// Contacts on an Edge device), is a real container to show.
	const sensorsByLine = useMemo<Record<string, ISensor[]>>(() => {
		const forDevice = filter(
			siteSensors || [],
			(sensor: ISensor) => String(sensor.device) === String(deviceId)
		);
		return groupBy(forDevice, 'line');
	}, [siteSensors, deviceId]);

	useEffect(() => {
		const userOrder =
			userPreferences?.siteSystemsOrderPerCustomer?.[customerId || ""]?.[
			siteId
			]?.[deviceId || ""];

		const items = [];
		systems?.forEach((entry: any) => {
			items.push({
				data: {
					...entry,
					highlightedId: `${deviceId}-${entry.id}`,
				},
				widgetType: "SiteSystem",
			});
		});

		Object.keys(sensorsByLine)
			.forEach((entry: any) => {
				items.push({
					data: {
						line: entry,
						sensors: sensorsByLine[entry],
						id: `AuxDevices-${entry}`,
						highlightedId: `${deviceId}-AuxDevices-${entry}`,
					},
					widgetType: "AuxDevices",
				});
			});

		if (numOfPowerMeters > 0) {
			items.push({
				data: {
					id: "PowerMeter",
					highlightedId: `${deviceId}-PowerMeter`,

				},
				widgetType: "PowerMeter",
			});
		}

		// Line 7 is reserved for modbus universal units (see modbusUnitsByLine
		// above) and must never surface as a tile, unassigned or otherwise.
		Object.keys(unassignedLines)
			.forEach((entry: any) => {
				items.push({
					data: {
						...unassignedLines[entry],
						line: entry,
						id: `UnassignedLines-${entry}`,
						highlightedId: `${deviceId}-UnassignedLines-${entry}`,

					},
					widgetType: "UnassignedLines",
				});
			});

		if (enableBacnet && bacnetUnits?.length > 0) {
			Object.keys(groupBy(filter(bacnetUnits, 'isEngaged'), 'line'))
				.forEach((entry: any) => {
					items.push({
						data: {
							line: entry,
							id: `BACnetDevices-${entry}`,
							highlightedId: `${deviceId}-BACnetDevices-${entry}`,
						},
						widgetType: "BACnetDevices",
					});
				});
		}

		if (enableModbus) {
			Object.keys(modbusUnitsByLine)
				.forEach((entry: any) => {
					items.push({
						data: {
							line: entry,
							modbusUnits: modbusUnitsByLine[entry],
							id: `ModbusDevices-${entry}`,
							highlightedId: `${deviceId}-ModbusDevices-${entry}`,
						},
						widgetType: "ModbusDevices",
					});
				});
		}

		//Sort objects based on their index in the array of IDs
		const result = sortBy(items, [
			(system) => {
				return userOrder && userOrder.includes(system?.data?.id)
					? userOrder.indexOf(system?.data?.id)
					: Infinity;
			},
			//@ts-ignore
			(entry) => entry.data?.id?.toLowerCase(),
		]);
		setTiles(result);
	}, [
		systems,
		unassignedLines,
		sensorsByLine,
		numOfPowerMeters,
		userPreferences,
		modbusUnitsByLine,
		enableModbus,
	]);

	// Drag and drop functions
	const saveUserOrder = async (tileIds: number[]): Promise<any> => {
		const currentStored = userPreferences?.siteSystemsOrderPerCustomer ? cloneDeep(userPreferences?.siteSystemsOrderPerCustomer) : {};
		const customer: string = customerId || "";
		const { id: deviceId } = device;
		return updateUserPreferences({
			siteSystemsOrderPerCustomer: setWith(currentStored, [customer, siteId, deviceId], tileIds, Object),
		});
	};

	const handleDragStart = (e: React.DragEvent<HTMLDivElement>, tile: any) => {
		setDraggedTile(tile);
		e.dataTransfer.effectAllowed = "move";
		e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>, tile: any) => {
		e.preventDefault();
		setDragOverTile(tile);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		if (
			draggedTile &&
			dragOverTile &&
			draggedTile.id !== dragOverTile.data.id
		) {
			const newTiles = tiles.filter(
				(t: any) => t.data.id !== draggedTile.data.id
			);
			const hoverIndex = newTiles.findIndex(
				(t: any) => t.data.id === dragOverTile.data.id
			);
			newTiles.splice(hoverIndex, 0, draggedTile);

			try {
				saveUserOrder(newTiles.map((entry: any) => entry.data.id));
			} catch (e) {
				console.error("An errror while saving user order", e);
			}

			setTiles(newTiles);
		}
		setDraggedTile(null);
		setDragOverTile(null);
	};

	const handleDragEnd = (id: string) => {
		setDraggedTile(null);
		setDragOverTile(null);
		setIsTileDraggable({
			...isTileDraggable,
			[id]: false
		});
	};

	const handleMouseDown = (e: any, id: string) => {
		if (!!e.target.closest(`.${classes.dragContainer}`)) {
			setIsTileDraggable({
				...isTileDraggable,
				[id]: true
			});
		}
	};

	const handleMouseUp = (id: string) => {
		setIsTileDraggable({
			...isTileDraggable,
			[id]: false
		});
	};

	// End drag and drop functions

	return (
		<div className={classes.SiteSystemsGrid}>
			{tiles.map((tile: { widgetType: string; data: any }) => (
				<div
					draggable={isTileDraggable?.[tile.data.id]}
					key={tile.data.id}
					onDragOver={(e) => handleDragOver(e, tile)}
					onDrop={handleDrop}
					onDragStart={(e) => handleDragStart(e, tile)}
					onDragEnd={(e: React.DragEvent<HTMLDivElement>) => {
						e.preventDefault();
						handleDragEnd(tile.data.id);
					}}
					className={clsx(classes.Tile, {
						Dragging: draggedTile?.data.id === tile.data.id,
						DragOver: dragOverTile?.data.id === tile.data.id,
						Highlighted: highlightedId === tile.data.highlightedId,
					})}
					onMouseDown={(e: any) => {
						handleMouseDown(e, tile.data.id);
					}}
					onMouseUp={() => {
						handleMouseUp(tile.data.id);
					}}
				>
					<div className={classes.dragContainer}>
						<MoreVert className={classes.whiteMoreVert} />
					</div>
					<div className={classes.baseTileWrapper} draggable={false}>
						<ViewWidget
							actions={actions}
							device={device}
							isService={isService}
							hasOnlyControlUnit={hasOnlyControlUnit}
							highlightedId={highlightedId || undefined}
							widgetType={tile.widgetType}
							data={tile.data}
						/>
					</div>
				</div>
			))}
			<div>
				<Grid item>
					<LightTooltip
						title={t`Manually add a system if it was not automatically detected by the application when connected with the CoolAutomation device.`}
					>
						<Button
							disabled={
								device?.canAddDevice || !device?.permissions?.canCreateSystems
							}
							disableRipple
							className={classes.addNewSystem}
							onClick={() => actions.openAddEditSystem(deviceId, true)}
						>
							<Add className={classes.addIcon} />
						</Button>
					</LightTooltip>
				</Grid>
			</div>
		</div>
	);
};

// @ts-ignore
export default SystemsView;
